import React, { useCallback, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { Autocomplete } from '@material-ui/lab';
import RetailPaymentService from 'src/services/retailPaymentService'
import { createAxiosInstance } from 'src/utils/axios';

const retailPaymentService = new RetailPaymentService(
    createAxiosInstance(process.env.REACT_APP_CASH_QORE_API)
);

const useStyles = makeStyles((theme) => ({
    dialogTitleConatiner: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.main,
    },
    dialogTitleText: {
        color: theme.palette.getContrastText(theme.palette.primary.dark),
        fontSize: theme.typography.h4.fontSize,
    },
}));

function PaymentReceiptDialog({ isDialogOpen, setIsDialogOpen, handleClose, retailPayment, onSuccess
}) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [filteredNotificationTypes, setFilteredNotificationTypes] = useState(['Email', 'SMS']);

    const validationSchema = Yup.object().shape({
        notificationType: Yup.string().required('Notification type is required'),
        phoneNumber: Yup.string()
            .when('notificationType', {
            is: 'sms',
            then: Yup.string()
                .required('Phone number is required')
                .matches(/^[0-9]{11}$/, 'Enter a valid 10-digit phone number'),
            otherwise: Yup.string().notRequired(),
            }),
        email: Yup.string().nullable()
            .when('notificationType', {
            is: 'email',
            then: Yup.string()
                .email('Enter a valid email')
                .required('Email is required'),
            otherwise: Yup.string().notRequired(),
            }),
    });

    const onSubmit = useCallback(async (values) => {
        try {
            const recieptResponse = await retailPaymentService.sendRetailPaymentReceipt(values);
            if (recieptResponse?.data){
                formik.resetForm();
                enqueueSnackbar('Receipt sent to customer.', {
                    variant: 'success',
                });

                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            enqueueSnackbar(`Error sending receipt to customer. ${error.message}`, { variant: 'error' });
        }
        finally {
            setIsDialogOpen(false);
        }
    }, [retailPayment]);

    const formik = useFormik({
        initialValues: {
            notificationType: '',
            email: retailPayment?.Caller?.Email,
            phoneNumber: retailPayment?.Caller?.Phone ?? '',
            transactionId: Number(retailPayment?.TransactionId),
            retailPaymentId: Number(retailPayment?.RetailPaymentId),
        },
        validationSchema: validationSchema,
        onSubmit,
    });

    return (
        <Dialog
            open={isDialogOpen}
            onClose={(_, reason) => handleClose(reason)}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle className={classes.dialogTitleConatiner}>
                <Typography className={classes.dialogTitleText}>
                    Send Receipt
                </Typography>
            </DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Typography style={{ marginBottom: '20px' }}>
                        How would you like to send the receipt?
                    </Typography>
                    <Grid container spacing={2} fullWidth>
                        <Grid item xs={12} spacing={2}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    fullWidth
                                    options={filteredNotificationTypes}
                                    disableClearable
                                    onChange={(event, value) => {
                                        formik.setFieldValue('notificationType', value ? value.toLowerCase() : '');  
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                            label="Notification Type"
                                            variant="outlined"
                                            fullWidth
                                            name='notificationType'
                                            error={formik.touched.notificationType && Boolean(formik.errors.notificationType)}
                                            helperText={formik.touched.notificationType && formik.errors.notificationType}
                                            InputProps={{
                                                ...params.InputProps,
                                                readOnly: true,
                                            }}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                      
                        {(formik.values.notificationType === 'sms') && (
                        <Grid item xs={12}>
                            <PhoneInput
                                country={'us'}
                                preferredCountries={["us"]}
                                onlyCountries={['us', 'ca', 'mx']}
                                value={formik.values.phoneNumber}
                                onChange={(phone) => {formik.setFieldValue('phoneNumber', phone); console.log("validation: ", formik.errors);}}
                                inputProps={{
                                    name: 'phoneNumber',
                                    required: true,
                                    autoFocus: true,
                                    onBlur: formik.handleBlur,
                                }}
                                inputStyle={{
                                    width: '100%',
                                    height: '56px',
                                    fontSize: '16px',
                                    borderColor: formik.touched.phoneNumber && formik.errors.phoneNumber ? '#f44336' : undefined,
                                }}
                            />
                        {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                            <div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
                                {formik.errors.phoneNumber}
                            </div>
                        )}
                        </Grid>
                        )}
                        {(formik.values.notificationType === 'email') && (
                            <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="email"
                                label="Email Address"
                                variant="outlined"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helpertext={formik.touched.email && formik.errors.email}
                            />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions
                    id="selected-records-actions"
                    data-test="selected-records-actions"
                    className={classes.dialogActions}
                >
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => {
                            setIsDialogOpen(false);
                            formik.resetForm();
                        }}
                        disabled={formik.isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        disabled={!formik.isValid || formik.isSubmitting}
                    >
                        {formik.isSubmitting && <CircularProgress size={24} style={{ marginRight: '12px' }} />}
                        Send Receipt
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

PaymentReceiptDialog.propTypes = {
    isDialogOpen: PropTypes.bool,
    setIsDialogOpen: PropTypes.func,
    handleClose: PropTypes.func,
    transactionId: PropTypes.number,
    onSuccess: PropTypes.func,
};


export default PaymentReceiptDialog;
